/**
 * PortfolioGrowthChart Component
 * Line chart tracking portfolio growth over time — Recharts / shadcn chart.
 */

import React, { useMemo, useEffect, useState, useRef, useCallback } from "react";
import { Area, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";
import { Box, Text } from "@chakra-ui/react";
import { DashboardWidgetLoadingChart } from "../../Dashboard/components/DashboardWidgetLoadingCard";
import { getDashboardWidgetLoadingMessage } from "../../Dashboard/components/dashboardWidgetLoadingMessages";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { usePortfolioGrowth } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import type { PortfolioGrowthResponse, PortfolioGrowthTransferBreakdown } from "../../../../services/tradeStatisticsApi";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { formatDisplayCurrency } from "../../../../utils/formatDisplayCurrency";
import {
  formatChartHourAxisLabel,
  formatYmdBucketShortInZone,
  formatYmBucketShortInZone,
  parseStatisticsChartInstant,
  shouldIncludeYearOnChartAxis,
} from "../../../../utils/displayDateTime";
import {
  getSamplingConfig,
  downsampleDataPoints,
  hourlyChartSpansMultipleDays,
  resolveChartTimeGrain,
} from "../../../../utils/chartDataSampling";
import { filterChartPointsToLocalRange } from "../../../../utils/chartLocalRangeFilter";
import {
  growthPointBaselineCumulativePnl,
  growthPointBaselineTransferBreakdown,
  growthPointChartValue,
  isTradingPnlOnlyGrowth,
} from "../../../../utils/portfolioGrowthUi";
import {
  STATS_FONT_FAMILY,
  useStatsCardHeadlineColor,
} from "../../../Dashboard/Statistics/components/statisticsCardHeadline";
import {
  STATS_CHART_TOOLTIP_TITLE_CLASS,
  StatisticsChartTooltipPnlRow,
  StatisticsChartTooltipRow,
  StatisticsChartTooltipTransferBreakdown,
  statsChartTooltipContentClassName,
} from "../../../Dashboard/Statistics/components/statisticsChartTooltip";
import { pnlBarFill } from "../../../Dashboard/Statistics/components/statsSignedPnlBarUtils";
import { ChessKingIcon } from "@/components/ui/chess-king-icon";
import type { TradingPnlSeries, ReportPnlBrushWindow } from "../../../../utils/reportTradingPnlSeries";
import {
  mergeTradingPnlSeriesForChart,
  tradingPnlSeriesHasData,
} from "../../../../utils/reportTradingPnlSeries";
import {
  computeEvenDayAxisTicks,
  computeTradingPnlYAxis,
  seriesValuesFromChartRows,
} from "../../../../utils/tradingPnlChartAxis";

function finiteNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Unique highest-PnL simulation (excludes baseline) for a merged chart row.
 * Returns null on ties — including when every series on the chart shares the same PnL.
 */
function winningSimIdAtRow(
  row: Record<string, number | string> | undefined,
  series: TradingPnlSeries[]
): string | null {
  if (!row) return null;

  const chartValues: number[] = [];
  for (const s of series) {
    const v = Number(row[s.id]);
    if (Number.isFinite(v)) chartValues.push(v);
  }
  if (
    chartValues.length >= 2 &&
    chartValues.every((v) => v === chartValues[0])
  ) {
    return null;
  }

  let bestId: string | null = null;
  let bestVal = -Infinity;
  let tiedForBest = false;
  for (const s of series) {
    if (s.id === "baseline") continue;
    const v = Number(row[s.id]);
    if (!Number.isFinite(v)) continue;
    if (v > bestVal) {
      bestVal = v;
      bestId = s.id;
      tiedForBest = false;
    } else if (v === bestVal) {
      tiedForBest = true;
    }
  }
  if (tiedForBest || bestId == null) return null;
  return bestId;
}

interface PortfolioGrowthChartProps {
  brokerAccountCode: string;
  timeframe?: import("../../../../services/tradeStatisticsApi").DashboardSummaryCardTimeframe;
  sampling?: "hourly" | "daily" | "weekly" | "monthly";
  chartLocalStart?: string | null;
  chartLocalEnd?: string | null;
  startDate?: Date;
  endDate?: Date;
  reportingTimezone?: string | null;
  useExternalDataOnly?: boolean;
  data?: PortfolioGrowthResponse | null;
  dataLoading?: boolean;
  brokerName?: string | null;
  supportsPortfolioGrowth?: boolean;
}

export interface ViewOptions {
  portfolioValue: boolean;
  tradingPnlOnly: boolean;
  transfers: boolean;
}

interface PortfolioGrowthChartInternalProps extends PortfolioGrowthChartProps {
  viewOptions?: ViewOptions;
  onViewOptionsChange?: (options: ViewOptions) => void;
}

const COLOR_PORTFOLIO_VALUE = "#17FEAE";
const COLOR_TRADING_PNL = "#38BBD1";
const COLOR_TRANSFERS = "#F49113";

const COMPARISON_CHART_HEIGHT = 300;
/** Prefer in-chart pan/zoom once the series is dense enough to need a window. */
const COMPARISON_PAN_MIN_POINTS = 12;
const COMPARISON_MIN_VISIBLE_POINTS = 8;
const COMPARISON_CHART_CLASS =
  "aspect-auto h-[300px] w-full min-w-0 overflow-hidden [&_.recharts-wrapper]:!overflow-hidden [&_.recharts-surface]:outline-none";

type ComparisonChartDatum = Record<string, number | string>;

type BrushRange = { startIndex: number; endIndex: number };

const TRADING_PNL_CHART_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
});

function formatTradingPnlChartDate(timeMs: number): string {
  return TRADING_PNL_CHART_DATE_FORMAT.format(new Date(timeMs));
}

function clampBrushRange(range: BrushRange, dataLength: number): BrushRange {
  if (dataLength <= 0) return { startIndex: 0, endIndex: 0 };
  const maxIdx = dataLength - 1;
  const startIndex = Math.min(Math.max(0, range.startIndex), maxIdx);
  const endIndex = Math.min(Math.max(startIndex, range.endIndex), maxIdx);
  return { startIndex, endIndex };
}

function fullBrushRange(dataLength: number): BrushRange {
  return { startIndex: 0, endIndex: Math.max(0, dataLength - 1) };
}

function brushIndexAtOrBefore(data: ComparisonChartDatum[], timeMs: number): number {
  let lo = 0;
  let hi = data.length - 1;
  let best = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const t = Number(data[mid]?.timeMs);
    if (!Number.isFinite(t) || t <= timeMs) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

function brushIndexAtOrAfter(data: ComparisonChartDatum[], timeMs: number): number {
  let lo = 0;
  let hi = data.length - 1;
  let best = data.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const t = Number(data[mid]?.timeMs);
    if (!Number.isFinite(t) || t >= timeMs) {
      best = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return best;
}

function brushWindowIsFull(
  window: ReportPnlBrushWindow | null,
  data: ComparisonChartDatum[]
): boolean {
  if (!window || data.length === 0) return true;
  const first = Number(data[0]?.timeMs);
  const last = Number(data[data.length - 1]?.timeMs);
  if (!Number.isFinite(first) || !Number.isFinite(last)) return true;
  return window.startMs <= first && window.endMs >= last;
}

function brushIndicesFromWindow(
  data: ComparisonChartDatum[],
  window: ReportPnlBrushWindow | null
): BrushRange {
  if (data.length === 0) return { startIndex: 0, endIndex: 0 };
  if (brushWindowIsFull(window, data)) return fullBrushRange(data.length);
  const startMs = window!.startMs;
  const endMs = window!.endMs;
  const startIndex = brushIndexAtOrBefore(data, startMs);
  const endIndex = Math.max(startIndex, brushIndexAtOrAfter(data, endMs));
  return clampBrushRange({ startIndex, endIndex }, data.length);
}

function windowFromIndices(
  data: ComparisonChartDatum[],
  range: BrushRange
): ReportPnlBrushWindow | null {
  if (data.length === 0) return null;
  const next = clampBrushRange(range, data.length);
  if (next.startIndex === 0 && next.endIndex === data.length - 1) return null;
  const startMs = Number(data[next.startIndex]?.timeMs);
  const endMs = Number(data[next.endIndex]?.timeMs);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  return { startMs, endMs };
}

export function PortfolioGrowthPnlComparisonChart({
  series,
  emptyMessage = "No closed trades with PnL in this window.",
  brushWindow,
  onBrushWindowChange,
}: {
  series: TradingPnlSeries[];
  emptyMessage?: string;
  brushWindow: ReportPnlBrushWindow | null;
  onBrushWindowChange: (window: ReportPnlBrushWindow | null) => void;
}): React.ReactElement {
  const { displayCurrency } = useDisplayCurrency();
  const axisTickColor = useStatsCardHeadlineColor();
  const chartHostRef = useRef<HTMLDivElement | null>(null);
  const panRef = useRef<{
    pointerId: number;
    startClientX: number;
    startRange: BrushRange;
    plotWidth: number;
  } | null>(null);

  const activeSeries = useMemo(
    () => series.filter((s) => s.points.length > 0),
    [series]
  );

  const chartConfig = useMemo((): ChartConfig => {
    const cfg: ChartConfig = {};
    for (const s of activeSeries) {
      cfg[s.id] = { label: s.label, color: s.color };
    }
    return cfg;
  }, [activeSeries]);

  const chartData = useMemo(
    () => mergeTradingPnlSeriesForChart(activeSeries) as ComparisonChartDatum[],
    [activeSeries]
  );

  const brushIndices = useMemo(
    () => brushIndicesFromWindow(chartData, brushWindow),
    [chartData, brushWindow]
  );

  const visibleChartData = useMemo(() => {
    const { startIndex, endIndex } = brushIndices;
    if (chartData.length === 0) return [];
    return chartData.slice(startIndex, endIndex + 1);
  }, [chartData, brushIndices]);

  const canPanZoom = chartData.length > COMPARISON_PAN_MIN_POINTS;
  const isZoomed = Boolean(
    canPanZoom && brushWindow && !brushWindowIsFull(brushWindow, chartData)
  );

  const yAxis = useMemo(() => {
    const ids = activeSeries.map((s) => s.id);
    return computeTradingPnlYAxis(seriesValuesFromChartRows(visibleChartData, ids));
  }, [activeSeries, visibleChartData]);

  const xDomain = useMemo((): [number, number] | undefined => {
    if (visibleChartData.length === 0) return undefined;
    const first = Number(visibleChartData[0]?.timeMs);
    const last = Number(visibleChartData[visibleChartData.length - 1]?.timeMs);
    if (!Number.isFinite(first) || !Number.isFinite(last)) return undefined;
    return first === last ? [first - 1, last + 1] : [first, last];
  }, [visibleChartData]);

  const xTicks = useMemo(() => {
    if (!xDomain) return undefined;
    return computeEvenDayAxisTicks(xDomain[0], xDomain[1], 6);
  }, [xDomain]);

  const finalPlotTimeMs = useMemo(() => {
    if (visibleChartData.length === 0) return null;
    const t = Number(visibleChartData[visibleChartData.length - 1]?.timeMs);
    return Number.isFinite(t) ? t : null;
  }, [visibleChartData]);

  const finalPlotWinnerSimId = useMemo(() => {
    if (visibleChartData.length === 0) return null;
    return winningSimIdAtRow(
      visibleChartData[visibleChartData.length - 1] as ComparisonChartDatum,
      activeSeries
    );
  }, [visibleChartData, activeSeries]);

  const formatXAxisTick = useCallback(
    (value: number | string) => formatTradingPnlChartDate(Number(value)),
    []
  );

  const applyRange = useCallback(
    (range: BrushRange) => {
      onBrushWindowChange(windowFromIndices(chartData, range));
    },
    [chartData, onBrushWindowChange]
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!canPanZoom || event.button !== 0) return;
      const host = chartHostRef.current;
      if (!host) return;
      const plotWidth = Math.max(1, host.getBoundingClientRect().width - 80);
      panRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startRange: { ...brushIndices },
        plotWidth,
      };
      host.setPointerCapture(event.pointerId);
    },
    [brushIndices, canPanZoom]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const pan = panRef.current;
      if (!pan || pan.pointerId !== event.pointerId || chartData.length === 0) return;

      const span = Math.max(1, pan.startRange.endIndex - pan.startRange.startIndex);
      const dx = event.clientX - pan.startClientX;
      const indexDelta = Math.round((-dx / pan.plotWidth) * span);
      if (indexDelta === 0) return;

      let startIndex = pan.startRange.startIndex + indexDelta;
      let endIndex = pan.startRange.endIndex + indexDelta;
      if (startIndex < 0) {
        endIndex -= startIndex;
        startIndex = 0;
      }
      if (endIndex > chartData.length - 1) {
        const overflow = endIndex - (chartData.length - 1);
        startIndex -= overflow;
        endIndex = chartData.length - 1;
      }
      applyRange(clampBrushRange({ startIndex, endIndex }, chartData.length));
    },
    [applyRange, chartData.length]
  );

  const endPan = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const pan = panRef.current;
    if (!pan || pan.pointerId !== event.pointerId) return;
    panRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // already released
    }
  }, []);

  const handleDoubleClick = useCallback(() => {
    if (!canPanZoom) return;
    onBrushWindowChange(null);
  }, [canPanZoom, onBrushWindowChange]);

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (!canPanZoom || chartData.length === 0) return;
      event.preventDefault();

      const { startIndex, endIndex } = brushIndices;
      const span = Math.max(1, endIndex - startIndex);
      const host = chartHostRef.current;
      const bounds = host?.getBoundingClientRect();
      const plotWidth = Math.max(1, (bounds?.width ?? 640) - 80);
      const relX = bounds
        ? Math.min(1, Math.max(0, (event.clientX - bounds.left - 56) / plotWidth))
        : 0.5;

      const zoomIn = event.deltaY < 0;
      const nextSpan = zoomIn
        ? Math.max(COMPARISON_MIN_VISIBLE_POINTS - 1, Math.floor(span * 0.8))
        : Math.min(chartData.length - 1, Math.ceil(span * 1.25));

      if (nextSpan >= chartData.length - 1) {
        onBrushWindowChange(null);
        return;
      }

      const focus = startIndex + relX * span;
      let nextStart = Math.round(focus - relX * nextSpan);
      let nextEnd = nextStart + nextSpan;
      if (nextStart < 0) {
        nextEnd -= nextStart;
        nextStart = 0;
      }
      if (nextEnd > chartData.length - 1) {
        const overflow = nextEnd - (chartData.length - 1);
        nextStart -= overflow;
        nextEnd = chartData.length - 1;
      }
      applyRange(clampBrushRange({ startIndex: nextStart, endIndex: nextEnd }, chartData.length));
    },
    [applyRange, brushIndices, canPanZoom, chartData.length, onBrushWindowChange]
  );

  useEffect(() => {
    const host = chartHostRef.current;
    if (!host) return;
    const onWheel = (event: WheelEvent) => handleWheel(event);
    host.addEventListener("wheel", onWheel, { passive: false });
    return () => host.removeEventListener("wheel", onWheel);
  }, [handleWheel]);

  const formatCurrency = (val: number) =>
    formatDisplayCurrency(finiteNumber(val), displayCurrency, { locale: "en-US" });

  const mainMargins = { top: 16, right: 24, left: 16, bottom: 12 };

  if (!tradingPnlSeriesHasData(activeSeries) || chartData.length === 0) {
    return (
      <Box
        w="100%"
        h={`${COMPARISON_CHART_HEIGHT}px`}
        minH={`${COMPARISON_CHART_HEIGHT}px`}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="sm" color="gray.500" fontFamily={STATS_FONT_FAMILY}>
          {emptyMessage}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      ref={chartHostRef}
      w="100%"
      minW={0}
      h={`${COMPARISON_CHART_HEIGHT}px`}
      minH={`${COMPARISON_CHART_HEIGHT}px`}
      position="relative"
      overflow="hidden"
      cursor={canPanZoom ? (isZoomed ? "grab" : "zoom-in") : "default"}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endPan}
      onPointerCancel={endPan}
      onDoubleClick={handleDoubleClick}
      title={
        canPanZoom
          ? "Scroll to zoom · drag to pan · double-click to reset"
          : undefined
      }
    >
      <ChartContainer
        config={chartConfig}
        className={COMPARISON_CHART_CLASS}
        style={{ height: COMPARISON_CHART_HEIGHT, fontFamily: STATS_FONT_FAMILY }}
      >
        <ComposedChart
          accessibilityLayer
          data={visibleChartData}
          margin={mainMargins}
        >
          <CartesianGrid vertical={false} strokeDasharray="4 4" className="stroke-border/50" />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={56}
            domain={yAxis.domain}
            ticks={yAxis.ticks}
            allowDataOverflow
            tick={{ fill: axisTickColor, fontSize: 11 }}
            tickFormatter={(val) => formatCurrency(val)}
          />
          <XAxis
            dataKey="timeMs"
            type="number"
            domain={xDomain}
            ticks={xTicks}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fill: axisTickColor, fontSize: 11 }}
            tickFormatter={formatXAxisTick}
          />
          <ChartTooltip
            cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1, strokeDasharray: "4 4" }}
            content={
              <ChartTooltipContent
                className={statsChartTooltipContentClassName("w-[220px]")}
                labelClassName={STATS_CHART_TOOLTIP_TITLE_CLASS}
                indicator="dot"
                labelFormatter={(_value, payload) => {
                  const row = payload?.[0]?.payload as ComparisonChartDatum | undefined;
                  const timeMs = Number(row?.timeMs);
                  if (Number.isFinite(timeMs)) return formatTradingPnlChartDate(timeMs);
                  return String(row?.label ?? "");
                }}
                formatter={(value, _name, item) => {
                  const seriesId = String(item?.dataKey ?? "");
                  const label =
                    chartConfig[seriesId]?.label ??
                    activeSeries.find((s) => s.id === seriesId)?.label ??
                    seriesId;
                  const seriesColor =
                    chartConfig[seriesId]?.color ??
                    activeSeries.find((s) => s.id === seriesId)?.color;
                  const n = Number(value);
                  const formatted = formatCurrency(n);
                  const row = item?.payload as ComparisonChartDatum | undefined;
                  const isFinalPlot =
                    finalPlotTimeMs != null &&
                    Number(row?.timeMs) === finalPlotTimeMs;
                  const showCrown =
                    isFinalPlot &&
                    finalPlotWinnerSimId != null &&
                    seriesId === finalPlotWinnerSimId;
                  return (
                    <StatisticsChartTooltipPnlRow
                      label={String(label)}
                      value={formatted}
                      color={pnlBarFill(n)}
                      seriesColor={seriesColor}
                      labelAdornment={
                        showCrown ? (
                          <ChessKingIcon
                            size={14}
                            className="text-amber-400"
                            aria-label="Highest PnL simulation"
                          />
                        ) : undefined
                      }
                    />
                  );
                }}
              />
            }
          />
          {activeSeries.map((s) => (
            <Line
              key={s.id}
              dataKey={s.id}
              name={s.label}
              type="monotone"
              stroke={s.color}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
              isAnimationActive={false}
            />
          ))}
        </ComposedChart>
      </ChartContainer>
    </Box>
  );
}

type ChartDatum = {
  label: string;
  portfolioValue?: number;
  tradingPnl?: number;
  transfers?: number;
  transferBreakdown?: PortfolioGrowthTransferBreakdown;
};

const PortfolioGrowthChart: React.FC<PortfolioGrowthChartInternalProps> = ({
  brokerAccountCode,
  timeframe = "today",
  sampling = "daily",
  chartLocalStart,
  chartLocalEnd,
  startDate,
  endDate,
  reportingTimezone,
  useExternalDataOnly,
  data: dataFromParent,
  dataLoading: dataLoadingFromParent,
  brokerName,
  supportsPortfolioGrowth,
  viewOptions: externalViewOptions,
  onViewOptionsChange,
}) => {
  const { displayCurrency, formatMoney } = useDisplayCurrency();
  const displayTimeZone = useDisplayTimeZone();
  const axisTickColor = useStatsCardHeadlineColor();

  const filters = useMemo(() => {
    const tz = reportingTimezone?.trim() || undefined;
    return {
      sampling,
      ...(startDate || endDate ? { startDate, endDate } : { timeframe }),
      ...(tz ? { timezone: tz } : {}),
    };
  }, [sampling, timeframe, startDate, endDate, reportingTimezone]);

  const skipHook = useExternalDataOnly === true;
  const { data: dataFromHook, loading: loadingFromHook, error, errorAction } = usePortfolioGrowth(
    brokerAccountCode,
    filters,
    {
      skip: skipHook,
      ...(brokerName !== undefined ? { brokerName } : {}),
      ...(supportsPortfolioGrowth !== undefined ? { supportsPortfolioGrowth } : {}),
    }
  );

  const data = skipHook ? (dataFromParent ?? null) : dataFromHook;
  const loading = skipHook ? (dataLoadingFromParent ?? false) : loadingFromHook;
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(
    skipHook ? null : error,
    skipHook ? null : errorAction
  );

  const [internalViewOptions, setInternalViewOptions] = useState<ViewOptions>({
    portfolioValue: true,
    tradingPnlOnly: true,
    transfers: true,
  });

  const viewOptions = externalViewOptions ?? internalViewOptions;

  useEffect(() => {
    if (!externalViewOptions) {
      const saved = localStorage.getItem("portfolioGrowthViewOptions");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setInternalViewOptions({ ...parsed, portfolioValue: true });
        } catch (e) {
          console.warn("Failed to parse saved view options", e);
        }
      }
    }
  }, [externalViewOptions]);

  useEffect(() => {
    if (!externalViewOptions) {
      localStorage.setItem("portfolioGrowthViewOptions", JSON.stringify(internalViewOptions));
    }
  }, [internalViewOptions, externalViewOptions]);

  const pnlOnlyGrowthCurve = useMemo(
    () => isTradingPnlOnlyGrowth(data, supportsPortfolioGrowth),
    [data, supportsPortfolioGrowth]
  );

  const chartViewOptions = useMemo((): ViewOptions => {
    if (pnlOnlyGrowthCurve) {
      return { portfolioValue: false, tradingPnlOnly: true, transfers: false };
    }
    return viewOptions;
  }, [pnlOnlyGrowthCurve, viewOptions]);

  const { chartData, chartConfig } = useMemo(() => {
    if (!data || !data.data_points || data.data_points.length === 0) {
      return { chartData: [] as ChartDatum[], chartConfig: {} as ChartConfig };
    }

    const scopedPoints = filterChartPointsToLocalRange(
      data.data_points,
      chartLocalStart,
      chartLocalEnd
    );
    if (scopedPoints.length === 0) {
      return { chartData: [] as ChartDatum[], chartConfig: {} as ChartConfig };
    }

    const config = getSamplingConfig(timeframe);
    const pointDates = scopedPoints.map((point) => point.date);
    const timeGrain = resolveChartTimeGrain({
      responseGrain: data.sampling,
      requestGrain: sampling,
      dates: pointDates,
    });
    const dailyFormat = timeGrain === "daily" || timeGrain === "weekly";
    const hourlyIncludeDay = timeGrain === "hourly" && hourlyChartSpansMultipleDays(pointDates);

    const dataPointsWithValues: any[] = [];
    const firstPoint = scopedPoints[0];

    const startingBalance = finiteNumber(data.starting_balance ?? firstPoint.opening_balance ?? 0);
    const baselineCumulativePnl = growthPointBaselineCumulativePnl(firstPoint);
    const startValue = pnlOnlyGrowthCurve ? baselineCumulativePnl : startingBalance;

    const baselineTransferBreakdown = growthPointBaselineTransferBreakdown(firstPoint);

    dataPointsWithValues.push({
      date: firstPoint.date,
      value: startValue,
      fullPoint: {
        ...firstPoint,
        portfolio_value: pnlOnlyGrowthCurve ? null : startingBalance,
        daily_pnl: 0,
        daily_transfers: 0,
        cumulative_pnl: baselineCumulativePnl,
        cumulative_transfers:
          finiteNumber(firstPoint.cumulative_transfers) -
          finiteNumber(firstPoint.daily_transfers),
        transfer_breakdown: baselineTransferBreakdown,
      },
      isOpeningBalance: true,
      labelSuffix: " (Start)",
    });

    scopedPoints.forEach((point) => {
      dataPointsWithValues.push({
        date: point.date,
        value: growthPointChartValue(point, pnlOnlyGrowthCurve),
        fullPoint: point,
        isOpeningBalance: false,
        labelSuffix: "",
      });
    });

    const sampled = downsampleDataPoints(dataPointsWithValues, config.maxDataPoints);

    if (sampled.length > 0 && !sampled[0].isOpeningBalance) {
      const startPoint = dataPointsWithValues.find((p: any) => p.isOpeningBalance);
      if (startPoint) {
        sampled.unshift(startPoint);
      }
    }

    const chartBuckets = scopedPoints.map((point) => {
      if (timeGrain === "hourly" || dailyFormat) {
        return point.date.length >= 10 ? point.date.slice(0, 10) : point.date.split("T")[0] || point.date;
      }
      return point.date.length >= 7 ? point.date.slice(0, 7) : point.date;
    });
    const includeYear =
      timeGrain !== "hourly" && dailyFormat
        ? shouldIncludeYearOnChartAxis(chartBuckets)
        : false;

    const rows: ChartDatum[] = sampled.map((point: any) => {
      let label: string;
      try {
        if (timeGrain === "hourly") {
          const parsed = parseStatisticsChartInstant(point.date);
          if (parsed) {
            label = formatChartHourAxisLabel(parsed, displayTimeZone, {
              includeDay: hourlyIncludeDay,
            });
          } else {
            label = point.date;
          }
        } else if (dailyFormat) {
          const ymd =
            point.date.length >= 10 ? point.date.slice(0, 10) : point.date.split("T")[0] || point.date;
          label = formatYmdBucketShortInZone(ymd, displayTimeZone, { includeYear });
        } else {
          const ym = point.date.length >= 7 ? point.date.slice(0, 7) : point.date;
          label = formatYmBucketShortInZone(ym, displayTimeZone);
        }
        if (point.isOpeningBalance) {
          label = `${label} (Start)`;
        }
      } catch {
        label = point.date + (point.labelSuffix || "");
      }

      const datum: ChartDatum = { label };

      if (chartViewOptions.portfolioValue) {
        datum.portfolioValue = finiteNumber(point.value);
      }
      if (chartViewOptions.tradingPnlOnly) {
        datum.tradingPnl = finiteNumber(point.fullPoint?.cumulative_pnl);
      }
      if (chartViewOptions.transfers) {
        datum.transfers = finiteNumber(
          point.fullPoint?.transfer_breakdown?.net_transfers ??
            point.fullPoint?.cumulative_transfers
        );
        datum.transferBreakdown = point.fullPoint?.transfer_breakdown;
      }

      return datum;
    });

    const cfg: ChartConfig = {};
    if (chartViewOptions.portfolioValue) {
      cfg.portfolioValue = { label: "Portfolio Value", color: COLOR_PORTFOLIO_VALUE };
    }
    if (chartViewOptions.tradingPnlOnly) {
      cfg.tradingPnl = {
        label: pnlOnlyGrowthCurve ? "Total P&L" : "Trading P&L Only",
        color: COLOR_TRADING_PNL,
      };
    }
    if (chartViewOptions.transfers) {
      cfg.transfers = { label: "Cumulative Transfers", color: COLOR_TRANSFERS };
    }

    return { chartData: rows, chartConfig: cfg };
  }, [
    data,
    sampling,
    chartViewOptions,
    displayTimeZone,
    timeframe,
    pnlOnlyGrowthCurve,
    chartLocalStart,
    chartLocalEnd,
  ]);

  const formatCurrency = (val: number) =>
    formatDisplayCurrency(finiteNumber(val), displayCurrency, { locale: "en-US" });

  const formatSignedCurrency = (val: number, options?: { forceSign?: boolean }) => {
    const n = finiteNumber(val);
    return formatDisplayCurrency(n, displayCurrency, {
      locale: "en-US",
      showSign: options?.forceSign === true || (n > 0 && options?.forceSign !== false),
    });
  };

  const transfersNetTooltipLabel =
    timeframe === "all-time" ? "Transfers (net)" : "Transfers (net, in range)";

  if (loading) {
    return (
      <Box
        id="portfolio-growth-chart"
        w="100%"
        h="100%"
        minH={0}
        flex="1"
        position="relative"
      >
        <DashboardWidgetLoadingChart
          ariaLabel={getDashboardWidgetLoadingMessage("portfolioGrowth")}
        />
      </Box>
    );
  }

  if (!skipHook && error && showWidgetError && widgetError) {
    return (
      <Box
        id="portfolio-growth-chart"
        w="100%"
        h="100%"
        minH={0}
        flex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={4}
      >
        <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} className="w-full max-w-lg" />
      </Box>
    );
  }

  if (chartData.length === 0) {
    return (
      <Box
        id="portfolio-growth-chart"
        w="100%"
        h="100%"
        minH={0}
        flex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="sm" color="gray.500" fontFamily={STATS_FONT_FAMILY}>
          {data && (!data.data_points || data.data_points.length === 0)
            ? "No data available for the selected timeframe"
            : "Select at least one view option to display the chart"}
        </Text>
      </Box>
    );
  }

  return (
    <Box id="portfolio-growth-chart" w="100%" h="100%" minH={0} flex="1" position="relative">
      <ChartContainer
        config={chartConfig}
        className="h-full w-full min-h-0"
        style={{ fontFamily: STATS_FONT_FAMILY }}
      >
        <ComposedChart
          accessibilityLayer
          data={chartData}
          margin={{ top: 16, right: 24, left: 16, bottom: 12 }}
        >
          <CartesianGrid vertical={false} strokeDasharray="4 4" className="stroke-border/50" />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={56}
            tick={{ fill: axisTickColor, fontSize: 11 }}
            tickFormatter={(val) => formatCurrency(val)}
          />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={40}
            interval="preserveStartEnd"
            tick={{ fill: axisTickColor, fontSize: 11 }}
          />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                className={statsChartTooltipContentClassName("w-[260px]")}
                labelClassName={STATS_CHART_TOOLTIP_TITLE_CLASS}
                indicator="dot"
                labelFormatter={(_value, payload) => {
                  const row = payload?.[0]?.payload as ChartDatum | undefined;
                  return row?.label ?? "";
                }}
                formatter={(value, name, item) => {
                  const n = Number(value);
                  const label = chartConfig[name as keyof typeof chartConfig]?.label ?? name;
                  const formatted = formatCurrency(n);
                  const row = item?.payload as ChartDatum | undefined;

                  if (name === "tradingPnl") {
                    return (
                      <StatisticsChartTooltipPnlRow
                        label={String(label)}
                        value={formatted}
                        color={pnlBarFill(n)}
                      />
                    );
                  }

                  if (name === "transfers") {
                    return (
                      <StatisticsChartTooltipTransferBreakdown
                        netLabel={transfersNetTooltipLabel}
                        netValue={formatted}
                        breakdown={row?.transferBreakdown}
                        seriesColor={COLOR_TRANSFERS}
                        formatSignedCurrency={formatSignedCurrency}
                      />
                    );
                  }

                  return (
                    <StatisticsChartTooltipRow label={String(label)} value={formatted} />
                  );
                }}
              />
            }
          />
          {chartViewOptions.portfolioValue && (
            <Area
              dataKey="portfolioValue"
              type="monotone"
              fill="transparent"
              stroke={COLOR_PORTFOLIO_VALUE}
              strokeWidth={2.5}
              isAnimationActive={false}
            />
          )}
          {chartViewOptions.tradingPnlOnly && (
            <Line
              dataKey="tradingPnl"
              type="monotone"
              stroke={COLOR_TRADING_PNL}
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
            />
          )}
          {chartViewOptions.transfers && (
            <Line
              dataKey="transfers"
              type="monotone"
              stroke={COLOR_TRANSFERS}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ChartContainer>
    </Box>
  );
};

export default PortfolioGrowthChart;
